import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Plus, X } from 'lucide-react';

import { cx } from '@/styles/muiTheme';

import { hintFor, pathToString } from './schemaValidation';

export type ValuePath = (string | number)[];

interface SchemaFormProps {
  /** Null for a key with no schema — the form renders an explanation instead. */
  schema: Record<string, any> | null;
  value: unknown;
  /** path -> message, keyed by {@link pathToString}. */
  errors: Record<string, string>;
  /** Replaces the value at `path`. The parent owns the draft. */
  onChange: (path: ValuePath, value: unknown) => void;
  /** Renders `format: 'secret'` fields as password inputs. */
  maskSecrets: boolean;
  disabled: boolean;
}

/**
 * Coerces an input's string back to the type the schema asks for.
 *
 * An empty numeric input becomes null rather than 0: 0 is a value an operator
 * might legitimately mean, so typing over a number must not momentarily assert
 * one. A non-numeric string is passed through untouched so the validator can
 * report it instead of the field silently swallowing the keystroke.
 */
const coerce = (raw: string, schema: Record<string, any>): unknown => {
  if (schema.type !== 'integer' && schema.type !== 'number') return raw;
  if (raw === '') return null;

  const parsed = Number(raw);
  return Number.isNaN(parsed) ? raw : parsed;
};

/** The value a freshly-added array item or object key starts at. */
const emptyFor = (schema: Record<string, any>): unknown => {
  if (schema.enum) return schema.enum[0];
  if (schema.type === 'integer' || schema.type === 'number') return schema.minimum ?? 0;
  if (schema.type === 'boolean') return false;
  if (schema.type === 'array') return [];
  if (schema.type === 'object') return {};
  return '';
};

const fieldSx = (hasError: boolean) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? cx.red.main : cx.border.strong,
  },
});

const Hint = ({ text, error }: { text: string; error?: string }) => (
  <Typography
    variant="caption"
    sx={{ color: error ? cx.red.main : cx.text.muted, display: 'block', marginTop: 0.5 }}
  >
    {error || text}
  </Typography>
);

const FieldLabel = ({ label, required }: { label: string; required: boolean }) => (
  <Typography variant="body2" sx={{ color: cx.text.secondary, fontWeight: 500 }}>
    {label}
    {required && (
      <Box component="span" sx={{ color: cx.gold, marginLeft: 0.5 }} title="Required">
        *
      </Box>
    )}
  </Typography>
);

/** A true/false pair, which reads better than a checkbox for a config flag. */
const BooleanField = ({
  value,
  onSet,
  disabled,
}: {
  value: unknown;
  onSet: (next: boolean) => void;
  disabled: boolean;
}) => (
  <Stack direction="row" sx={{ gap: 0.5 }}>
    {[true, false].map((option) => {
      const active = value === option;

      return (
        <Button
          key={String(option)}
          size="small"
          disabled={disabled}
          onClick={() => onSet(option)}
          sx={{
            minWidth: 64,
            backgroundColor: active ? cx.blue.main : 'transparent',
            color: active ? cx.text.primary : cx.text.muted,
            border: `1px solid ${active ? cx.blue.main : cx.border.strong}`,
            '&:hover': { backgroundColor: active ? cx.blue.dark : cx.bg.elevated },
          }}
        >
          {option ? 'True' : 'False'}
        </Button>
      );
    })}
  </Stack>
);

/** A scalar array: one input per item, plus add/remove. */
const ScalarArrayField = ({
  schema,
  value,
  path,
  errors,
  onChange,
  disabled,
}: {
  schema: Record<string, any>;
  value: unknown;
  path: ValuePath;
  errors: Record<string, string>;
  onChange: (path: ValuePath, value: unknown) => void;
  disabled: boolean;
}) => {
  const items = Array.isArray(value) ? value : [];
  const itemSchema = schema.items as Record<string, any>;
  const canAdd = schema.maxItems == null || items.length < schema.maxItems;

  return (
    <Stack sx={{ gap: 0.75 }}>
      {items.map((item, index) => {
        const itemPath = [...path, index];
        const itemError = errors[pathToString(itemPath)];

        return (
          // The index is the identity here: these are positional slots in an
          // array, and reordering is not an operation this form offers.
          <Stack key={index} direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
            <TextField
              select={!!itemSchema.enum}
              size="small"
              disabled={disabled}
              type={
                itemSchema.type === 'integer' || itemSchema.type === 'number' ? 'number' : 'text'
              }
              value={item == null ? '' : String(item)}
              onChange={(event) => onChange(itemPath, coerce(event.target.value, itemSchema))}
              sx={{ ...fieldSx(!!itemError), width: 320 }}
            >
              {(itemSchema.enum || []).map((option: unknown) => (
                <MenuItem key={String(option)} value={String(option)}>
                  {String(option)}
                </MenuItem>
              ))}
            </TextField>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() =>
                onChange(
                  path,
                  items.filter((_, other) => other !== index),
                )
              }
              aria-label={`Remove item ${index + 1}`}
            >
              <X size={14} />
            </IconButton>
          </Stack>
        );
      })}

      {canAdd && (
        <Button
          size="small"
          disabled={disabled}
          startIcon={<Plus size={14} />}
          onClick={() => onChange(path, [...items, emptyFor(itemSchema)])}
          sx={{ alignSelf: 'flex-start', color: cx.text.tertiary }}
        >
          Add item
        </Button>
      )}
    </Stack>
  );
};

/** One leaf field, dispatched on the schema node's shape. */
const Field = ({
  label,
  schema,
  value,
  path,
  required,
  errors,
  onChange,
  maskSecrets,
  disabled,
}: {
  label: string;
  schema: Record<string, any>;
  value: unknown;
  path: ValuePath;
  required: boolean;
  errors: Record<string, string>;
  onChange: (path: ValuePath, value: unknown) => void;
  maskSecrets: boolean;
  disabled: boolean;
}) => {
  const error = errors[pathToString(path)];

  const control = (() => {
    if (schema.type === 'boolean') {
      return (
        <BooleanField value={value} disabled={disabled} onSet={(next) => onChange(path, next)} />
      );
    }

    if (schema.enum) {
      return (
        <TextField
          select
          size="small"
          disabled={disabled}
          value={value == null ? '' : String(value)}
          onChange={(event) => onChange(path, coerce(event.target.value, schema))}
          sx={{ ...fieldSx(!!error), width: 320 }}
        >
          {schema.enum.map((option: unknown) => (
            <MenuItem key={String(option)} value={String(option)}>
              {String(option)}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (
      schema.type === 'array' &&
      schema.items?.type !== 'object' &&
      schema.items?.type !== 'array'
    ) {
      return (
        <ScalarArrayField
          schema={schema}
          value={value}
          path={path}
          errors={errors}
          onChange={onChange}
          disabled={disabled}
        />
      );
    }

    if (schema.type === 'string' || schema.type === 'integer' || schema.type === 'number') {
      const isSecret = schema.format === 'secret' && maskSecrets;

      return (
        <TextField
          size="small"
          disabled={disabled}
          type={isSecret ? 'password' : schema.type === 'string' ? 'text' : 'number'}
          value={value == null ? '' : String(value)}
          onChange={(event) => onChange(path, coerce(event.target.value, schema))}
          sx={{ ...fieldSx(!!error), width: schema.type === 'string' ? 520 : 200 }}
        />
      );
    }

    // Everything the form does not model — arrays of objects, untyped nodes,
    // anything using allOf/oneOf. Editing the raw JSON is honest about what is
    // happening; a half-rendered widget would not be.
    return (
      <TextField
        multiline
        minRows={3}
        disabled={disabled}
        value={value === undefined ? '' : JSON.stringify(value, null, 2)}
        onChange={(event) => {
          try {
            onChange(path, JSON.parse(event.target.value));
          } catch {
            // Swallowed on purpose: a half-typed document is invalid on nearly
            // every keystroke, and the JSON tab is where syntax gets reported.
          }
        }}
        sx={{
          ...fieldSx(!!error),
          width: 520,
          '& textarea': { fontFamily: cx.fontMono, fontSize: 12 },
        }}
      />
    );
  })();

  const isRaw = !['boolean', 'string', 'integer', 'number', 'array'].includes(schema.type);

  return (
    <Box
      sx={{
        padding: 1,
        borderRadius: 1,
        backgroundColor: error ? 'rgba(240,68,56,0.06)' : 'transparent',
      }}
    >
      <FieldLabel label={label} required={required} />
      <Box sx={{ marginTop: 0.75 }}>{control}</Box>
      <Hint text={`${hintFor(schema)}${isRaw ? ' · edit as JSON' : ''}`} error={error} />
    </Box>
  );
};

/**
 * Renders a value as a form, driven entirely by its JSON Schema.
 *
 * Nested objects recurse into an indented group. There is no "add a key" action:
 * the schema defines the shape, so the set of editable keys is fixed. Adding a
 * key means editing the schema, which is a separate, deliberate act.
 */
const SchemaForm = ({
  schema,
  value,
  errors,
  onChange,
  maskSecrets,
  disabled,
  path = [],
  depth = 0,
}: SchemaFormProps & { path?: ValuePath; depth?: number }) => {
  // A key with no schema has no fields to render. This used to fall through to
  // <Field schema={null}>, which throws on `schema.type` — the optional chain
  // below guards the comparison but not the node it then hands on.
  if (!schema || typeof schema !== 'object') {
    return (
      <Alert severity="info">
        This key has no schema, so there is no form to render. Edit it on the JSON tab, where it is
        checked for being valid JSON.
      </Alert>
    );
  }

  if (schema.type !== 'object') {
    return (
      <Field
        label="value"
        schema={schema}
        value={value}
        path={path}
        required
        errors={errors}
        onChange={onChange}
        maskSecrets={maskSecrets}
        disabled={disabled}
      />
    );
  }

  const properties: Record<string, Record<string, any>> = schema.properties || {};
  const required: string[] = schema.required || [];
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

  return (
    <Stack sx={{ gap: 0.5, paddingLeft: depth ? 2 : 0 }}>
      {Object.keys(properties).map((key) => {
        const child = properties[key];
        const childPath = [...path, key];

        if (child.type === 'object') {
          return (
            <Box key={key} sx={{ marginTop: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: cx.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  fontFamily: cx.fontMono,
                }}
              >
                {key}
              </Typography>
              <Box
                sx={{
                  borderLeft: `1px solid ${cx.border.subtle}`,
                  marginTop: 0.5,
                  paddingLeft: 1,
                }}
              >
                <SchemaForm
                  schema={child}
                  value={record[key]}
                  errors={errors}
                  onChange={onChange}
                  maskSecrets={maskSecrets}
                  disabled={disabled}
                  path={childPath}
                  depth={depth + 1}
                />
              </Box>
            </Box>
          );
        }

        return (
          <Field
            key={key}
            label={key}
            schema={child}
            value={record[key]}
            path={childPath}
            required={required.includes(key)}
            errors={errors}
            onChange={onChange}
            maskSecrets={maskSecrets}
            disabled={disabled}
          />
        );
      })}
    </Stack>
  );
};

export default SchemaForm;
